import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef } from 'react';
import { type DayButton, DayPicker, type Locale } from 'react-day-picker';
import { ptBR } from 'react-day-picker/locale';

import { cn } from '@/shared/utils/cn';

/**
 * Props do componente Calendar — herda todas as props do `DayPicker` v9.
 */
export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Calendar baseado em `react-day-picker` v9 estilizado com tokens do design system.
 *
 * Renderiza um calendário standalone com navegação de mês, suporte a seleção
 * single/range/multiple e localização. Usa `ptBR` como locale padrão.
 *
 * @example
 * ```tsx
 * <Calendar mode="single" selected={date} onSelect={setDate} />
 * ```
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  locale = ptBR,
  formatters,
  components,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      locale={locale}
      className={cn('bg-ctx-layout-surface p-mini', className)}
      formatters={{
        formatMonthDropdown: date => date.toLocaleString(locale?.code, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: 'w-fit rounded-md p-nano',
        months: 'relative flex flex-col gap-mini md:flex-row',
        month: 'flex w-full flex-col gap-mini',
        nav: 'absolute inset-x-none top-none flex w-full items-center justify-between gap-quark',
        button_previous:
          'rounded-sm p-quark text-ctx-content-base hover:bg-ctx-layout-spotlight aria-disabled:opacity-medium aria-disabled:pointer-events-none',
        button_next:
          'rounded-sm p-quark text-ctx-content-base hover:bg-ctx-layout-spotlight aria-disabled:opacity-medium aria-disabled:pointer-events-none',
        month_caption:
          'flex h-xs w-full items-center justify-center px-xs ts-subtitle-xxs text-ctx-content-title',
        dropdowns:
          'flex h-xs w-full items-center justify-center gap-quark ts-paragraph-xxs font-medium',
        dropdown_root: 'relative rounded-sm border-hairline border-ctx-layout-border',
        dropdown: 'absolute inset-none bg-ctx-layout-surface opacity-transparent',
        caption_label: cn(
          'select-none ts-subtitle-xxs text-ctx-content-title',
          captionLayout !== 'label' &&
            'flex items-center gap-quark rounded-sm [&>svg]:size-[0.875rem] [&>svg]:text-ctx-content-placeholder',
        ),
        table: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'flex-1 select-none rounded-sm ts-subtitle-xxxs text-ctx-content-placeholder',
        week: 'mt-quark flex w-full',
        week_number_header: 'w-[2.25rem] select-none',
        week_number: 'select-none ts-paragraph-xxs text-ctx-content-placeholder',
        day: 'group/day relative aspect-square h-xs w-xs select-none rounded-sm p-none text-center ts-paragraph-xs text-ctx-content-base',
        range_start:
          'rounded-l-sm bg-ctx-interactive-primary text-ctx-interactive-primaryAlternative',
        range_middle: 'rounded-none bg-ctx-layout-spotlight',
        range_end:
          'rounded-r-sm bg-ctx-interactive-primary text-ctx-interactive-primaryAlternative',
        today:
          'rounded-sm border-hairline border-ctx-interactive-primary text-ctx-interactive-primary',
        outside: 'text-ctx-content-placeholder opacity-medium',
        disabled: 'text-ctx-content-placeholder opacity-medium pointer-events-none',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Root: ({ className: rootClassName, rootRef, ...rootProps }) => (
          <div data-slot="calendar" ref={rootRef} className={cn(rootClassName)} {...rootProps} />
        ),
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={cn('size-mini text-ctx-content-title', chevronClassName)}
                {...chevronProps}
              />
            );
          }
          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('size-mini text-ctx-content-title', chevronClassName)}
                {...chevronProps}
              />
            );
          }
          return (
            <ChevronDownIcon
              className={cn('size-mini text-ctx-content-title', chevronClassName)}
              {...chevronProps}
            />
          );
        },
        DayButton: dayProps => <CalendarDayButton locale={locale} {...dayProps} />,
        WeekNumber: ({ children, ...weekNumberProps }) => (
          <td {...weekNumberProps}>
            <div className="flex h-xs w-xs items-center justify-center text-center">{children}</div>
          </td>
        ),
        ...components,
      }}
      {...props}
    />
  );
}

interface CalendarDayButtonProps extends React.ComponentProps<typeof DayButton> {
  readonly locale?: Partial<Locale>;
}

/**
 * Botão de dia do calendário com suporte a estados de seleção single/range.
 *
 * Componente interno usado pelo `Calendar` para renderizar cada célula de dia.
 * Aplica estilos para os estados `range_start`, `range_middle`, `range_end` e
 * seleção única, e propaga foco quando a célula está focada.
 */
export function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: CalendarDayButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);

  const {
    selected,
    focused,
    range_start: rangeStart,
    range_end: rangeEnd,
    range_middle: rangeMiddle,
  } = modifiers;

  useEffect(() => {
    if (focused) ref.current?.focus();
  }, [focused]);

  const isSelectedSingle = selected && !rangeStart && !rangeEnd && !rangeMiddle;

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={isSelectedSingle}
      data-range-start={rangeStart}
      data-range-end={rangeEnd}
      data-range-middle={rangeMiddle}
      className={cn(
        'relative z-10 flex aspect-square h-xs w-xs flex-col items-center justify-center gap-quark rounded-sm border-none ts-paragraph-xxs text-ctx-content-base transition hover:bg-ctx-layout-spotlight focus-visible:outline-none focus-visible:ring-[0.25rem] focus-visible:ring-ctx-highlight-ringFocusShadow',
        'data-[selected-single=true]:bg-ctx-interactive-primary data-[selected-single=true]:text-ctx-interactive-primaryAlternative',
        'data-[range-start=true]:bg-ctx-interactive-primary data-[range-start=true]:text-ctx-interactive-primaryAlternative data-[range-start=true]:rounded-l-sm',
        'data-[range-middle=true]:bg-ctx-layout-spotlight data-[range-middle=true]:text-ctx-content-base data-[range-middle=true]:rounded-none',
        'data-[range-end=true]:bg-ctx-interactive-primary data-[range-end=true]:text-ctx-interactive-primaryAlternative data-[range-end=true]:rounded-r-sm',
        '[&>span]:ts-paragraph-xxs [&>span]:opacity-medium',
        className,
      )}
      {...props}
    />
  );
}
